import React, { useEffect, useMemo, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { userRequest } from "../../utils/requestMethods";
import { getTeacherName } from "../../utils/classGroupHelpers";

// Used two ways: from a teacher's profile (teacher fixed, pick a class), or from a class
// (class fixed via initialClassId + lockClass, no `teacher` — pick one from `teachers`).
const AssignClassModal = ({
  onClose,
  teacher,
  school,
  onSuccess,
  initialClassId = "",
  lockClass = false,
  initialClassName = "",
  teachers = [],
  initialRole = "",
}) => {
  const [classId, setClassId] = useState(initialClassId);
  const [teacherId, setTeacherId] = useState(teacher?._id || "");
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [roleLevel, setRoleLevel] = useState(initialRole);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchClasses = async () => {
      if (!school?.code) return;

      try {
        setLoadingClasses(true);
        const { data } = await userRequest.get(`classes?code=${school?.code}`);
        if (isMounted) setClasses(data.classes || []);
      } catch (error) {
        if (isMounted) console.error("Error fetching classes:", error);
      } finally {
        if (isMounted) setLoadingClasses(false);
      }
    };

    fetchClasses();

    return () => {
      isMounted = false;
    };
  }, [school?.code]);

  // Fetch role levels
  useEffect(() => {
    let isMounted = true;
    const fetchRoles = async () => {
      try {
        setLoadingRoles(true);
        const { data } = await userRequest.get(`/teachers/roles-levels`);
        if (isMounted) setAvailableRoles(data.roleLevels || []);
      } catch (err) {
        if (isMounted) console.error("Error fetching role levels:", err);
      } finally {
        if (isMounted) setLoadingRoles(false);
      }
    };
    fetchRoles();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch all permissions
  useEffect(() => {
    let isMounted = true;
    const fetchPerms = async () => {
      try {
        setLoadingPermissions(true);
        const { data } = await userRequest.get(`/teachers/permissions`);
        if (isMounted) setAvailablePermissions(data.permissions || []);
      } catch (err) {
        if (isMounted) console.error("Error fetching permissions:", err);
      } finally {
        if (isMounted) setLoadingPermissions(false);
      }
    };
    fetchPerms();
    return () => {
      isMounted = false;
    };
  }, []);

  // Pre-check default permissions when a role is selected
  useEffect(() => {
    let isMounted = true;
    const fetchDefaultPerms = async () => {
      if (!roleLevel) return;
      try {
        const { data } = await userRequest.get(`/teachers/default-permissions`, {
          params: { role: roleLevel, assignmentType: 'class' }
        });
        const defaults = (data.permissions || []).map(p => p.value);
        if (isMounted) setSelectedPermissions(defaults);
      } catch (err) {
        if (isMounted) console.error("Error fetching default permissions:", err);
      }
    };
    fetchDefaultPerms();
    return () => {
      isMounted = false;
    };
  }, [roleLevel]);

  const permissionsByCategory = useMemo(() => {
    const map = {};
    for (const perm of availablePermissions) {
      const cat = perm.category || 'Other';
      if (!map[cat]) map[cat] = [];
      map[cat].push(perm);
    }
    return map;
  }, [availablePermissions]);

  const togglePermission = (value) => {
    setSelectedPermissions(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!teacherId) {
      setError("Please select a teacher.");
      return;
    }
    if (!classId) {
      setError("Please select a class.");
      return;
    }
    if (!roleLevel) {
      setError("Please select a role.");
      return;
    }
    try {
      setLoading(true);
      await userRequest.post(`/teachers/assign-teacher-to-class`, {
        schoolId: school._id,
        teacherId,
        classId,
        roleLevel,
        permissions: selectedPermissions,
      });
      if (onSuccess) onSuccess();
      //setSuccess("Class assigned successfully!");
      onClose(); // Close the modal after a successful submission
    } catch (error) {
      console.error("Error assigning class:", error);
      const payload = error?.response?.data;
      let message = "An unexpected error occurred.";
      if (typeof payload === 'string') {
        message = payload;
      } else if (payload && (payload.message || payload.error)) {
        message = payload.message || payload.error;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full md:w-1/3 p-6 shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            {teacher ? "Assign as Class Teacher" : "Choose a Class Teacher"}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes />
          </button>
        </div>

        {/* Error or Success Message */}
        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
        {success && <p className="text-green-500 text-sm mb-4">{success}</p>}

        {/* Teacher (only when opened from a class) */}
        {!teacher && (
          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">Select Teacher</label>
            <select
              onChange={(e) => setTeacherId(e.target.value)}
              value={teacherId}
              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              disabled={loading}
            >
              <option value="" disabled>
                {teachers.length > 0 ? "Select a Teacher" : "No teachers available"}
              </option>
              {teachers.map((t) => (
                <option key={t._id} value={t._id}>
                  {getTeacherName(teachers, t._id)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Dropdown */}
        <div className="mb-4">
          <label className="block text-sm text-gray-600 mb-2">{lockClass ? "Class" : "Select Class"}</label>
          {lockClass ? (
            <p className="w-full border border-gray-200 bg-gray-50 rounded-lg p-2 text-sm text-gray-800">
              {initialClassName ||
                classes.find((c) => c._id === classId)?.class_name ||
                (loadingClasses ? "Loading class..." : "Selected class")}
            </p>
          ) : (
            <select
              onChange={(e) => setClassId(e.target.value)}
              value={classId}
              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              disabled={loading}
            >
              <option value="" disabled>
                {loadingClasses ? "Loading classes..." : "Select a Class"}
              </option>
              {classes.length > 0 ? (
                classes.map((classe) => (
                  <option key={classe._id} value={classe._id}>
                    {classe.class_name}
                  </option>
                ))
              ) : (
                <option disabled>No classes available</option>
              )}
            </select>
          )}
        </div>

        {/* Role */}
        <div className="mb-4">
          <label className="block text-sm text-gray-600 mb-2">Select Role</label>
          {loadingRoles ? (
            <div className="text-xs text-gray-500 p-4 border border-gray-200 rounded-lg bg-gray-50">
              Loading roles...
            </div>
          ) : availableRoles.length === 0 ? (
            <div className="text-xs text-gray-500 p-4 border border-gray-200 rounded-lg bg-gray-50">
              No roles available.
            </div>
          ) : (
            <div className='flex flex-wrap gap-3'>
              {availableRoles.map(optionRole => (
                <label
                  key={optionRole.value}
                  className={`flex-1 min-w-[200px] flex items-center justify-between border rounded-lg px-4 py-3 text-sm cursor-pointer transition-colors ${
                    roleLevel === optionRole.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-slate-200 hover:border-indigo-400'
                  }`}
                >
                  <div className='flex flex-col text-left flex-1'>
                    <span className='font-medium'>{optionRole.label}</span>
                    {optionRole.description && (
                      <span className='text-xs text-slate-500 mt-1'>
                        {optionRole.description}
                      </span>
                    )}
                  </div>
                  <input
                    type='radio'
                    name='teacherRole'
                    value={optionRole.value}
                    checked={roleLevel === optionRole.value}
                    onChange={(e) => setRoleLevel(e.target.value)}
                    disabled={loading}
                    className='form-radio text-indigo-600 ml-2'
                  />
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Permissions */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm text-gray-600">Permissions</label>
            <button
              type="button"
              className="text-xs text-indigo-600 hover:underline disabled:opacity-60"
              onClick={() => setSelectedPermissions(availablePermissions.map(p => p.value))}
              disabled={loadingPermissions || availablePermissions.length === 0}
            >
              Select All
            </button>
          </div>
          {loadingPermissions ? (
            <div className="text-xs text-gray-500">Loading permissions...</div>
          ) : (
            <div className="max-h-48 overflow-auto border border-gray-200 rounded p-2">
              {Object.keys(permissionsByCategory).map((cat) => (
                <div key={cat} className="mb-3">
                  <p className="text-xs font-semibold text-gray-700 mb-1">{cat}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {permissionsByCategory[cat].map((perm) => (
                      <label key={perm.value} className="flex items-center gap-2 text-xs text-gray-700">
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={selectedPermissions.includes(perm.value)}
                          onChange={() => togglePermission(perm.value)}
                          disabled={loading}
                        />
                        <span>{perm.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {availablePermissions.length === 0 && (
                <div className="text-xs text-gray-500">No permissions available.</div>
              )}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="text-right">
            <button 
              onClick={submitHandler} 
              className="px-5 py-3 bg-[#3F3DA5] text-white text-sm font-semibold rounded-full hover:bg-indigo-800 transition disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={loading}
            >
                {loading ? "Assigning..." : "Assign Now"}
            </button>
        </div>
      </div>
    </div>
  );
};

export default AssignClassModal;
